import { FC, useMemo } from "react";
import orderOptimizerRailyDetailsDriverTableHelper from "./order-optimizer-raily-details-driver-table.helper";
import OrderOptimizerRailyDetailsDriverTableRow from "../common/types/order-optimizer-raily-details-driver-table-row";
import OrderOptimizerRailyDetailsDriverTableRates from "./types/order-optimizer-raily-details-driver-table-rates";
import NumericInputComponent from "../../../../../common/components/form/input/numeric-input/numeric-input.component";
import TableComponent, {
  TableProps,
} from "../../../../../common/components/table/table.component";
import FormValidationResult from "../../../../../common/utils/validation/types/form-validation-result";
import { useAppContext } from "../../../../../context/app.context";
import orderOptimizerTranslationsHelper from "../../../../../languages/order-optimizer-translations.helper";
import OrderOptimizerRailyDetailsItem from "../common/types/order-optimizer-raily-details-item";
import orderOptimizerRailyDetailsHelper from "../order-optimizer-raily-details.helper";

type OrderOptimizerRailyDetailsDriverTableProps = Pick<
  TableProps,
  "isError" | "isLoading"
> & {
  detailsItem?: OrderOptimizerRailyDetailsItem;
  marginAba: number;
  marginSabs: number;
  validationResult: Record<
    keyof OrderOptimizerRailyDetailsDriverTableRates,
    FormValidationResult | null
  >;
  onRateAbaChanged: (key: string, rateAba: number) => void;
  onRateSabsChanged: (key: string, rateSabs: number) => void;
  onRateSaAndBsChanged: (key: string, rateSaAndBs: number) => void;
  rates: OrderOptimizerRailyDetailsDriverTableRates;
};

const OrderOptimizerRailyDetailsDriverTableComponent: FC<
  OrderOptimizerRailyDetailsDriverTableProps
> = (props) => {
  const { selectedAppLanguage } = useAppContext();

  const columns = useMemo(() => {
    return orderOptimizerRailyDetailsDriverTableHelper.getColumns();
  }, [selectedAppLanguage]);

  const createOrdersByDriverAbaTableRow = (
    detailsItem: OrderOptimizerRailyDetailsItem,
  ): OrderOptimizerRailyDetailsDriverTableRow => {
    const distanceLabel = orderOptimizerRailyDetailsHelper.getDistanceLabel(
      detailsItem.ordersByDriverAba?.distance,
    );
    const distanceTitle = orderOptimizerRailyDetailsHelper.getDistanceLabel(
      detailsItem.ordersByDriverAba?.distance,
    );

    const totalTimeLabel = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsItem.ordersByDriverAba?.totalTime,
    );
    const totalTimeTitle = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsItem.ordersByDriverAba?.totalTime,
    );

    const haltingTimeLabel = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsItem.ordersByDriverAba?.haltingTime,
    );
    const haltingTimeTitle = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsItem.ordersByDriverAba?.haltingTime,
    );

    const contractRateTitle = orderOptimizerRailyDetailsHelper.getRateLabel(
      props.rates.aba,
    );

    const abaAndSabsMarginLabel =
      orderOptimizerRailyDetailsHelper.getMarginLabel(props.marginAba);
    const abaAndSabsMarginTitle =
      orderOptimizerRailyDetailsHelper.getMarginLabel(props.marginAba);

    const translations =
      orderOptimizerTranslationsHelper.getRailyDetailsTranslations().table
        .driver;

    return {
      id: "aba",
      value: {
        driver: (
          <div title={detailsItem.driver?.displayName ?? "---"}>
            {detailsItem.driver?.displayName ?? "---"}
          </div>
        ),
        billingModel: <div title="ABA">ABA</div>,
        contractRate: (
          <div title={contractRateTitle}>
            <NumericInputComponent
              value={props.rates.aba}
              onChange={(value) =>
                props.onRateAbaChanged("aba", value ?? props.rates.aba)
              }
              decimalPrecision={2}
              hasError={!!props.validationResult.aba?.errorMessage}
              placeholder={translations.ratePlaceholderLabel}
            />
            {!!props.validationResult.aba?.errorMessage && (
              <span className={"route_details_error_message"}>
                {props.validationResult.aba.errorMessage}
              </span>
            )}
          </div>
        ),
        totalTime: <div title={totalTimeTitle}>{totalTimeLabel}</div>,
        haltingTime: (
          <div
            title={haltingTimeTitle}
            className={
              (detailsItem.ordersByDriverAba?.haltingTime ?? 0) < 0
                ? "time_delayed"
                : ""
            }
          >
            {haltingTimeLabel}
          </div>
        ),
        distance: <div title={distanceTitle}>{distanceLabel}</div>,
        margin: (
          <div title={abaAndSabsMarginTitle}>{abaAndSabsMarginLabel}</div>
        ),
      },
    };
  };

  const createOrdersByDriverSabsTableRow = (
    detailsItem: OrderOptimizerRailyDetailsItem,
  ): OrderOptimizerRailyDetailsDriverTableRow => {
    const distanceLabel = orderOptimizerRailyDetailsHelper.getDistanceLabel(
      detailsItem.ordersByDriverSabs?.distance,
    );
    const distanceTitle = orderOptimizerRailyDetailsHelper.getDistanceLabel(
      detailsItem.ordersByDriverSabs?.distance,
    );

    const totalTimeLabel = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsItem.ordersByDriverSabs?.totalTime,
    );
    const totalTimeTitle = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsItem.ordersByDriverSabs?.totalTime,
    );

    const haltingTimeLabel = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsItem.ordersByDriverSabs?.haltingTime,
    );
    const haltingTimeTitle = orderOptimizerRailyDetailsHelper.getTimeDeltaLabel(
      detailsItem.ordersByDriverSabs?.haltingTime,
    );

    const contractRateTitle = orderOptimizerRailyDetailsHelper.getRateLabel(
      props.rates.sabs,
    );

    const abaAndSabsMarginLabel =
      orderOptimizerRailyDetailsHelper.getMarginLabel(props.marginSabs);
    const abaAndSabsMarginTitle =
      orderOptimizerRailyDetailsHelper.getMarginLabel(props.marginSabs);

    const translations =
      orderOptimizerTranslationsHelper.getRailyDetailsTranslations().table
        .driver;

    return {
      id: "sabs",
      value: {
        driver: (
          <div title={detailsItem.driver?.displayName ?? "---"}>
            {detailsItem.driver?.displayName ?? "---"}
          </div>
        ),
        billingModel: <div title="SABS">SABS</div>,
        contractRate: (
          <div title={contractRateTitle}>
            <NumericInputComponent
              value={props.rates.sabs}
              onChange={(value) =>
                props.onRateSabsChanged("sabs", value ?? props.rates.sabs)
              }
              decimalPrecision={2}
              hasError={!!props.validationResult.sabs?.errorMessage}
              placeholder={translations.ratePlaceholderLabel}
            />
            {props.validationResult.sabs?.errorMessage && (
              <span className={"route_details_error_message"}>
                {props.validationResult.sabs.errorMessage}
              </span>
            )}
          </div>
        ),
        totalTime: <div title={totalTimeTitle}>{totalTimeLabel}</div>,
        haltingTime: (
          <div
            title={haltingTimeTitle}
            className={
              (detailsItem.ordersByDriverSabs?.haltingTime ?? 0) < 0
                ? "time_delayed"
                : ""
            }
          >
            {haltingTimeLabel}
          </div>
        ),
        distance: <div title={distanceTitle}>{distanceLabel}</div>,
        margin: (
          <div title={abaAndSabsMarginTitle}>{abaAndSabsMarginLabel}</div>
        ),
      },
    };
  };

  const createSaAndBsDistanceTableRow = (
    detailsItem: OrderOptimizerRailyDetailsItem,
  ): OrderOptimizerRailyDetailsDriverTableRow => {
    const sabsAbaDifferenceDistance =
      (detailsItem.ordersByDriverSabs?.distance ?? 0) -
      (detailsItem.ordersByDriverAba?.distance ?? 0);

    const distanceLabel = orderOptimizerRailyDetailsHelper.getDistanceLabel(
      sabsAbaDifferenceDistance,
    );
    const distanceTitle = orderOptimizerRailyDetailsHelper.getDistanceLabel(
      sabsAbaDifferenceDistance,
    );

    const contractRateTitle = orderOptimizerRailyDetailsHelper.getRateLabel(
      props.rates.saAndBs,
    );

    const translations =
      orderOptimizerTranslationsHelper.getRailyDetailsTranslations().table
        .driver;

    return {
      id: "saAndBs",
      value: {
        driver: (
          <div title={translations.saAndBsDistanceTitle}>
            {translations.saAndBsDistanceLabel}
          </div>
        ),
        billingModel: <div title="---">---</div>,
        contractRate: (
          <div title={contractRateTitle}>
            <NumericInputComponent
              value={props.rates.saAndBs}
              onChange={(value) =>
                props.onRateSaAndBsChanged(
                  "saAndBs",
                  value ?? props.rates.saAndBs,
                )
              }
              decimalPrecision={2}
              hasError={!!props.validationResult.saAndBs?.errorMessage}
              placeholder={translations.ratePlaceholderLabel}
            />
            {props.validationResult.saAndBs?.errorMessage && (
              <span className={"route_details_error_message"}>
                {props.validationResult.saAndBs.errorMessage}
              </span>
            )}
          </div>
        ),
        totalTime: <div title="---">---</div>,
        haltingTime: <div title="---">---</div>,
        distance: <div title={distanceTitle}>{distanceLabel}</div>,
        margin: <div title="---">---</div>,
      },
    };
  };

  const rows: OrderOptimizerRailyDetailsDriverTableRow[] = useMemo(() => {
    if (!props.detailsItem) return [];

    return [
      createOrdersByDriverAbaTableRow(props.detailsItem),
      createOrdersByDriverSabsTableRow(props.detailsItem),
      createSaAndBsDistanceTableRow(props.detailsItem),
    ];
  }, [props.rates, props.detailsItem, selectedAppLanguage]);

  return (
    <TableComponent
      columns={columns}
      rows={rows}
      isLoading={props.isLoading}
      isError={props.isError}
    />
  );
};

export default OrderOptimizerRailyDetailsDriverTableComponent;
